import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Search, Edit, Clock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';

const Production = () => {
  const [flows, setFlows] = useState([]);
  const [filteredFlows, setFilteredFlows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [addQuantityDialogOpen, setAddQuantityDialogOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: '', location: '' });
  const [stageUpdate, setStageUpdate] = useState({ quantity_completed: '' });
  const [addQuantityForm, setAddQuantityForm] = useState({ stage: '', quantity: '', location: '' });
  const [assignedTo, setAssignedTo] = useState('');
  const [locations, setLocations] = useState([]);

  const stages = ['printing', 'filling', 'sealing', 'packaging', 'ready_to_dispatch'];

  useEffect(() => {
    fetchFlows();
    fetchUsers();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = flows.filter(f =>
        f.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.so_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFlows(filtered);
    } else {
      setFilteredFlows(flows);
    }
    setCurrentPage(1);
  }, [searchTerm, flows]);

  const fetchFlows = async () => {
    try {
      // Use combined endpoint that includes both production flows and production orders
      const response = await axios.get(`${API}/production-tracking/all`);
      setFlows(response.data.filter(f => f.current_stage !== 'completed'));
      setFilteredFlows(response.data.filter(f => f.current_stage !== 'completed'));
    } catch (error) {
      toast.error('Failed to fetch production tracking');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/warehouse-locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleAssign = (flow) => {
    setEditingFlow(flow);
    setAssignedTo(flow.assigned_to || '');
    setAssignDialogOpen(true);
  };

  const submitAssignment = async () => {
    try {
      // Check if it's a production order or production flow
      const endpoint = editingFlow.source_type === 'production_order'
        ? `${API}/production-orders/${editingFlow.id}/assign?assigned_to=${assignedTo}`
        : `${API}/production-flows/${editingFlow.id}/assign?assigned_to=${assignedTo}`;
      await axios.put(endpoint);
      toast.success('Production assigned');
      setAssignDialogOpen(false);
      fetchFlows();
    } catch (error) {
      toast.error('Failed to assign production');
    }
  };

  const handleSearch = async () => {
    // Use client-side filtering which already covers production orders
    if (!searchTerm) {
      setFilteredFlows(flows);
    }
    // The useEffect already handles the filtering
  };

  const updateStage = async (flowId, stage, completed) => {
    const flow = flows.find(f => f.id === flowId);
    const stageData = flow.stage_quantities?.[stage] || { completed: 0, received: 0 };
    const currentCompleted = typeof stageData === 'object' ? stageData.completed || 0 : stageData;
    const received = typeof stageData === 'object' ? stageData.received || 0 : flow.quantity;
    const pending = received - currentCompleted;

    const stageHistoryItem = [...(flow.stage_history || [])].reverse().find(h => h.stage === stage);
    const defaultLocation = stageHistoryItem?.location || flow.location || '';

    setEditingFlow(flow);
    setAddQuantityForm({
      stage,
      quantity: pending.toString(),
      currentCompleted,
      received,
      pending,
      location: defaultLocation
    });
    setAddQuantityDialogOpen(true);
  };

  const confirmAddQuantity = async () => {
    const { stage, quantity } = addQuantityForm;
    const quantityToAdd = parseFloat(quantity) || 0;

    if (quantityToAdd <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (!addQuantityForm.location) {
      toast.error('Location is required');
      return;
    }

    try {
      // Check if this will complete the stage (all quantity done)
      const stageData = editingFlow.stage_quantities?.[stage] || { completed: 0, received: 0 };
      const currentCompleted = typeof stageData === 'object' ? stageData.completed || 0 : 0;
      const received = typeof stageData === 'object' ? stageData.received || 0 : editingFlow.quantity;
      const newTotal = currentCompleted + quantityToAdd;

      // Stage is complete ONLY if the total completed meets or exceeds the entire requested order quantity
      const isStageComplete = newTotal >= editingFlow.quantity;

      const payload = {
        stage,
        completed: isStageComplete,
        quantity_completed: quantityToAdd,
        location: addQuantityForm.location || ''
      };

      // Use appropriate endpoint based on source type
      const endpoint = editingFlow.source_type === 'production_order'
        ? `${API}/production-orders/${editingFlow.id}/stage`
        : `${API}/production-flows/${editingFlow.id}/stage`;

      await axios.put(endpoint, payload);

      if (isStageComplete) {
        toast.success(`${stage.replace('_', ' ')} stage completed! ${quantityToAdd} units processed.`);
      } else {
        toast.success(`Added ${quantityToAdd} units to ${stage.replace('_', ' ')}. ${received - newTotal} remaining.`);
      }

      setAddQuantityDialogOpen(false);
      fetchFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const confirmStageUpdate = async () => {
    const { flowId, stage, completed } = window.currentStageUpdate;

    try {
      const payload = {
        stage,
        completed,
        quantity_completed: stageUpdate.quantity_completed ? parseFloat(stageUpdate.quantity_completed) : null
      };

      await axios.put(`${API}/production-flows/${flowId}/stage`, payload);
      toast.success('Stage updated successfully');
      setQuantityDialogOpen(false);
      fetchFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleEdit = (flow) => {
    setEditingFlow(flow);
    setEditForm({ quantity: flow.quantity, location: flow.location || '' });
    setEditDialogOpen(true);
  };

  const submitEdit = async () => {
    if (!editForm.location) {
      toast.error('Location is required');
      return;
    }
    try {
      await axios.put(`${API}/production-flows/${editingFlow.id}?quantity=${editForm.quantity}&location=${editForm.location}`);
      toast.success('Production flow updated');
      setEditDialogOpen(false);
      fetchFlows();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="production-page">
      <div className="mb-6">
        <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Production Tracking</h1>
        <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Monitor and update production stages</p>
      </div>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by product name, SO number, or PO number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
            data-testid="search-production"
          />
        </div>
        <Button onClick={handleSearch} data-testid="search-button">
          Search
        </Button>
      </div>

      <div className="space-y-4">
        {filteredFlows.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((flow) => (
          <div key={flow.id} className="bg-white rounded-sm border border-stone-200 shadow-sm p-6" data-testid={`production-flow-${flow.id}`}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-[hsl(145,60%,10%)]" style={{ fontFamily: 'Manrope, sans-serif' }}>{flow.product_name}</h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${flow.source_type === 'production_order' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                    {flow.source_type === 'production_order' ? 'Production Order' : 'Sales Order'}
                  </span>
                  {flow.priority && flow.priority !== 'normal' && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${flow.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      flow.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                      {flow.priority.toUpperCase()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[hsl(145,20%,40%)]">
                  {flow.reference_number || flow.so_id} | Quantity: {flow.quantity} | Location: {flow.location || 'N/A'}
                </p>
                {flow.assigned_to && (
                  <p className="text-xs text-[hsl(145,60%,20%)] mt-1">
                    Assigned to: {users.find(u => u.id === flow.assigned_to)?.name || flow.assigned_to}
                  </p>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[hsl(145,60%,20%)]/10 text-[hsl(145,60%,20%)]">
                  {flow.current_stage.replace('_', ' ').toUpperCase()}
                </span>
                <Button variant="ghost" size="sm" onClick={() => handleAssign(flow)} data-testid={`assign-flow-${flow.id}`}>
                  <UserPlus size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(flow)} data-testid={`edit-flow-${flow.id}`}>
                  <Edit size={14} />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {stages.map((stage, idx) => {
                const isCompleted = flow.stages[stage];
                const isCurrent = flow.current_stage === stage;
                const stageData = flow.stage_quantities?.[stage] || { completed: 0, received: 0 };
                const completed = typeof stageData === 'object' ? stageData.completed || 0 : stageData;
                const received = typeof stageData === 'object' ? stageData.received || 0 : flow.quantity;
                const pending = received - completed;
                const hasWork = received > 0;
                const canWork = hasWork && !isCompleted && (isCurrent || stages.indexOf(flow.current_stage) > idx);

                const stageHistoryItem = [...(flow.stage_history || [])].reverse().find(h => h.stage === stage);
                const displayLocation = stageHistoryItem?.location || flow.location;

                return (
                  <div key={stage} className="flex items-center flex-1">
                    <div className="flex flex-col items-center w-full">
                      <button
                        onClick={() => hasWork && !isCompleted && updateStage(flow.id, stage, true)}
                        className={`w-12 h-12 rounded-sm flex flex-col items-center justify-center transition-colors duration-200 ${isCompleted
                          ? 'bg-[hsl(145,60%,20%)] text-white'
                          : isCurrent && hasWork
                            ? 'bg-[hsl(145,60%,20%)]/20 border-2 border-[hsl(145,60%,20%)] text-[hsl(145,60%,20%)] cursor-pointer hover:bg-[hsl(145,60%,20%)]/30'
                            : hasWork && pending > 0
                              ? 'bg-orange-100 border-2 border-orange-400 text-orange-600 cursor-pointer hover:bg-orange-200'
                              : 'bg-stone-100 text-stone-400'
                          }`}
                        disabled={!hasWork || isCompleted}
                        title={hasWork ? `Click to add completed quantity` : 'No work received yet'}
                        data-testid={`stage-${stage}-${flow.id}`}
                      >
                        {isCompleted ? (
                          <Check size={20} />
                        ) : hasWork && pending > 0 ? (
                          <span className="text-xs font-bold">+</span>
                        ) : null}
                      </button>
                      <p className="text-xs text-[hsl(145,20%,40%)] mt-2 text-center font-medium">
                        {stage.replace('_', ' ').toUpperCase()}
                      </p>
                      {displayLocation && (
                        <p className="text-[10px] text-stone-500 mt-1 text-center truncate max-w-[80px]" title={displayLocation}>
                          Loc: {displayLocation}
                        </p>
                      )}
                      {/* Stage quantity details */}
                      <div className="text-xs text-center mt-1">
                        {hasWork ? (
                          <>
                            <div className="flex items-center justify-center gap-1">
                              <span className={`font-bold ${isCompleted ? 'text-green-600' : 'text-blue-600'}`}>{completed}</span>
                              <span className="text-stone-400">/</span>
                              <span className="text-stone-500">{received}</span>
                            </div>
                            {pending > 0 && !isCompleted && (
                              <span className="block text-orange-600 font-bold animate-pulse">
                                {pending} pending
                              </span>
                            )}
                            {isCompleted && (
                              <span className="block text-green-600 font-medium">Done</span>
                            )}
                          </>
                        ) : (
                          <span className="text-stone-400">Waiting</span>
                        )}
                      </div>
                    </div>
                    {idx < stages.length - 1 && (
                      <div className={`h-0.5 flex-1 mx-2 ${isCompleted ? 'bg-[hsl(145,60%,20%)]' : 'bg-stone-200'
                        }`} />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-[hsl(145,20%,40%)]">
              <Clock size={12} />
              <span>Last updated: {new Date(flow.updated_at).toLocaleString()}</span>
            </div>
          </div>
        ))}

        {filteredFlows.length === 0 && (
          <div className="bg-white rounded-sm border border-stone-200 shadow-sm p-12 text-center" data-testid="no-production-flows">
            <p className="text-[hsl(145,20%,40%)]">No active production flows</p>
          </div>
        )}
      </div>
      <div className="mt-4">
        <Pagination currentPage={currentPage} totalItems={filteredFlows.length} onPageChange={setCurrentPage} />
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Production Flow</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Production Quantity</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.quantity}
                onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
              />
              <p className="text-xs text-[hsl(145,20%,40%)] mt-1">Total quantity to produce (can be more than order quantity)</p>
            </div>
            <div>
              <Label>Location <span className="text-red-500">*</span></Label>
              <Input
                value={editForm.location}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                placeholder="Warehouse A, Line 1, etc."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={submitEdit}>Update</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={quantityDialogOpen} onOpenChange={setQuantityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Stage Completion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[hsl(145,20%,40%)]">
              Enter the quantity completed for this stage. Leave empty to mark entire quantity as complete.
            </p>
            <div>
              <Label>Quantity Completed (Optional)</Label>
              <Input
                type="number"
                step="0.01"
                value={stageUpdate.quantity_completed}
                onChange={(e) => setStageUpdate({ quantity_completed: e.target.value })}
                placeholder={`Total: ${editingFlow?.quantity || 0}`}
              />
              <p className="text-xs text-[hsl(145,20%,40%)] mt-1">
                Example: If 1000 out of 3000 pieces are done, enter 1000
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setQuantityDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirmStageUpdate}>Confirm</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Production Flow</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Assign to User</Label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full p-2 border border-stone-200 rounded-sm"
              >
                <option value="">Select a user...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={submitAssignment} disabled={!assignedTo}>Assign</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Quantity Dialog - For partial completion over multiple days */}
      <Dialog open={addQuantityDialogOpen} onOpenChange={setAddQuantityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Completed Quantity - {addQuantityForm.stage?.replace('_', ' ').toUpperCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-sm">
              <p className="text-sm font-medium text-[hsl(145,60%,10%)]">{editingFlow?.product_name}</p>
              <p className="text-xs text-[hsl(145,20%,40%)]">SO: {editingFlow?.so_id}</p>
              <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)]">Already Done</p>
                  <p className="font-bold text-green-600">{addQuantityForm.currentCompleted}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)]">Total Received</p>
                  <p className="font-bold text-blue-600">{addQuantityForm.received}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)]">Pending</p>
                  <p className="font-bold text-orange-600">{addQuantityForm.pending}</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Quantity Completed Today</Label>
              <Input
                type="number"
                step="1"
                value={addQuantityForm.quantity}
                onChange={(e) => setAddQuantityForm({ ...addQuantityForm, quantity: e.target.value })}
                placeholder={`Enter quantity (max: ${addQuantityForm.pending})`}
                max={addQuantityForm.pending}
              />
              <p className="text-xs text-[hsl(145,20%,40%)] mt-1">
                Enter how many units you completed in this session
              </p>
            </div>

            <div>
              <Label>Location <span className="text-red-500">*</span></Label>
              <Select
                value={addQuantityForm.location}
                onValueChange={(value) => setAddQuantityForm({ ...addQuantityForm, location: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(l => (
                    <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {parseFloat(addQuantityForm.quantity) >= addQuantityForm.pending && addQuantityForm.pending > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                <p className="text-sm text-green-700 font-medium">
                  This will complete the {addQuantityForm.stage?.replace('_', ' ')} stage and move to the next stage!
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddQuantityDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={confirmAddQuantity}
                className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                disabled={!addQuantityForm.quantity || parseFloat(addQuantityForm.quantity) <= 0}
              >
                Add Completed Quantity
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Production;
